import { getRoutes } from "../utilities/routes";

// Types
export interface Process {
  id: number;
  title: string;
  serviceRef: number;
  serviceName?: string;
  createdOn: string;
  modifiedOn: string;
  selectedSections?: number[];
  stages?: Stage[];
  reasonSolutions?: ReasonSolution[];
}

export interface Stage {
  id: number;
  stageNumber: number;
  title: string;
  sectionRef: number;
  isPublic?: boolean;
  followUpGroupIdsJson?: string;
}

export interface ReasonSolution {
  id: number;
  row: number;
  isPrimary: boolean;
  reason: string;
  solution: string;
  previousReasonIdsJson?: string;
  stageRef: number;
}

export interface Service {
  id: number;
  name: string;
}

export interface CreateProcessRequest {
  title: string;
  serviceRef: number;
  selectedSections?: number[];
  stages?: any[];
  reasonSolutions?: any[];
}

export interface UpdateProcessRequest {
  title: string;
  serviceRef: number;
}

export interface ApiResponse<T> {
  status: boolean;
  data: T;
  message?: string;
}

// API functions
export const fetchProcesses = async (token: string): Promise<Process[]> => {
  const routes = getRoutes();
  const response = await fetch(routes.ProcessesRoutes, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage =
      errorData?.message ||
      errorData?.title ||
      `Failed to fetch processes: ${response.statusText}`;
    throw new Error(errorMessage);
  }

  return response.json();
};

export const fetchProcessById = async (
  id: number,
  token: string
): Promise<Process> => {
  const routes = getRoutes();
  const response = await fetch(`${routes.ProcessesRoutes}${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage =
      errorData?.message ||
      errorData?.title ||
      `Failed to fetch process: ${response.statusText}`;
    throw new Error(errorMessage);
  }

  return response.json();
};

export const fetchServices = async (token: string): Promise<Service[]> => {
  const routes = getRoutes();
  const response = await fetch(routes.ServicesRoutes, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage =
      errorData?.message ||
      errorData?.title ||
      `Failed to fetch services: ${response.statusText}`;
    throw new Error(errorMessage);
  }

  return response.json();
};

export const createProcess = async (
  data: CreateProcessRequest,
  token: string
): Promise<ApiResponse<Process>> => {
  const routes = getRoutes();
  const response = await fetch(routes.ProcessesRoutes, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage =
      errorData?.message ||
      errorData?.title ||
      `Failed to create process: ${response.statusText}`;
    throw new Error(errorMessage);
  }

  return response.json();
};

export const createCompleteProcess = async (
  data: CreateProcessRequest,
  token: string
): Promise<ApiResponse<Process>> => {
  const routes = getRoutes();
  console.log("📤 API Request URL:", `${routes.ProcessesRoutes}complete`);
  console.log("📤 API Request Data:", JSON.stringify(data, null, 2));

  const response = await fetch(`${routes.ProcessesRoutes}complete`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage =
      errorData?.message ||
      errorData?.title ||
      `Failed to create complete process: ${response.statusText}`;
    throw new Error(errorMessage);
  }

  return response.json();
};

export const updateProcess = async (
  id: number,
  data: UpdateProcessRequest,
  token: string
): Promise<ApiResponse<Process>> => {
  const routes = getRoutes();
  const response = await fetch(`${routes.ProcessesRoutes}${id}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage =
      errorData?.message ||
      errorData?.title ||
      `Failed to update process: ${response.statusText}`;
    throw new Error(errorMessage);
  }

  return response.json();
};

export const deleteProcess = async (
  id: number,
  token: string
): Promise<ApiResponse<string>> => {
  const routes = getRoutes();
  const response = await fetch(`${routes.ProcessesRoutes}${id}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage =
      errorData?.message ||
      errorData?.title ||
      `Failed to delete process: ${response.statusText}`;
    throw new Error(errorMessage);
  }

  return response.json();
};
